<template>
  <v-card>
    <v-card-title class="pa-8 pb-6">
      {{ $t('components.employeeFormImport.title') }}
    </v-card-title>
    <v-card-text class="px-8" v-if="!showProgress">
      <v-row>
        <v-col>
          <div class="label mb-1">{{ $t('components.employeeFormImport.account') }}</div>
          <account-select-field :corporation-id="corporation" @change="changeCorporation" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="label mb-1">{{ $t('components.employeeFormImport.file') }}</div>
          <v-file-input outlined filled hide-details show-size
                        prepend-icon=""
                        v-model="file"
                        accept=".csv, text/csv"
                        @change="loadFile"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>{{ $t('components.employeeFormImport.explanation') }} <i>{{ $t('components.employeeFormImport.export') }}</i>.</v-col>
      </v-row>
    </v-card-text>
    <v-card-text class="px-8" v-else>
      <v-row>
        <v-col>
          <div class="label mb-1">{{ $t('components.employeeFormImport.progress') }}</div>
          <v-progress-linear height="25"
                             v-model="importProgress"
          >
            <template v-slot:default="{ value }">
              {{ Math.ceil(value) }}%
            </template>
          </v-progress-linear>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="label mb-1">{{ $t('components.employeeFormImport.summary.status') }}</div>
          <v-list class="summary">
            <v-list-item v-if="summary.imported">
              <v-list-item-content>{{ $t('components.employeeFormImport.summary.imported', { count: summary.imported }) }}</v-list-item-content>
            </v-list-item>
            <v-list-item v-if="summary.existing">
              <v-list-item-content >{{ $t('components.employeeFormImport.summary.existing', { count: summary.existing }) }}</v-list-item-content>
            </v-list-item>
            <v-list-item v-if="summary.failed.count">
              <v-list-item-content>
                {{ $t('components.employeeFormImport.summary.failed', { count: summary.failed.count }) }}

                <v-list class="errors-list">
                  <v-list-item v-for="(error, index) in summary.failed.errors" :key="index">
                    <v-list-item-content>{{ `${index + 1}. ${handleErrorDisplayMessage(error)}` }}</v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="justify-end pa-8 pt-3">
      <v-btn text plain large class="pa-3 pl-0 cancel" @click="cancel"
             :disabled="importing"
      >
        {{ $t('components.employeeFormImport.cancel') }}
      </v-btn>
      <v-spacer />
      <v-btn dark large class="pa-3" color="#333333" elevation="0"
             :disabled="!corporation || !showProgress && !importData.length || importing"
             @click="doImport"
      >
        {{ showProgress
        ? $t('components.employeeFormImport.done')
        : importData.length
          ? $t('components.employeeFormImport.add_count', { count: importData.length })
          : $t('components.employeeFormImport.add') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import Papa from 'papaparse';
import PQueue from 'p-queue';
import {BusinessBookerEvent} from "@/core/tracking/businessBookerEvent/BusinessBookerEvent";
import {BusinessBookerEventCode} from "@/core/tracking/businessBookerEvent/BusinessBookerEventCode";
import AccountSelectField from '@/components/corporations/v2/AccountSelectField.vue';

const IMPORT_SUMMARY_DEFAULT = {
  imported: 0,
  failed: {
    errors: [],
    count: 0,
  },
  existing: 0,
};

export default {
  components: {
    AccountSelectField,
    // CorporationsSelect,
  },
  data: () => ({
    importing: false,
    showProgress: false,
    file: null,
    importData: [],
    importProgress: 0,
    corporations: [],
    corporation: null,
    corporationsLoading: false,
    trackingErrors: [],
    summary: { ...IMPORT_SUMMARY_DEFAULT },
  }),
  async mounted() {
    this.corporationsLoading = true;
    const { data } = await this.$store.dispatch('corporations/listCorporations', { maxPerPage: 100, sort: 'name' });
    this.corporations = data ?? [];
    this.resetCorporation();
    this.corporationsLoading = false;
  },
  computed: {
    ...mapGetters({
      preselectedCorporation: 'corporations/getSelectedCorporationId',
    }),
  },
  watch: {
    preselectedCorporation() {
      this.resetCorporation();
    },
  },
  methods: {
    reset() {
      this.importProgress = 0;
      this.file = null;
      this.importData = [];
      this.showProgress = false;
      this.corporation = this.preselectedCorporation;
      this.summary = {
        ...IMPORT_SUMMARY_DEFAULT,
        failed: {
          count: 0,
          errors: [],
        }
      };
      this.trackingErrors = [];
    },
    cancel() {
      this.$emit('cancel');
      this.reset();
    },
    resetCorporation() {
      if (this.preselectedCorporation) this.corporation = this.preselectedCorporation;
      else this.corporation = null;
    },
    async loadFile(file) {
      const phone = (countryCode, number) => {
        const phoneCountryCode = (countryCode && !countryCode.startsWith('+') && !countryCode.startsWith('0') ? `+${countryCode}` : countryCode);
        return phoneCountryCode + number;
      };
      if (file) {
        this.importData = (await this.parseCsv(file)).data.map((user, index) => {
          const hasEmptyStrings = user.some((value) => value === '');
          if (index === 0 || hasEmptyStrings) return;

          return ({
            givenName: user[0],
            familyName: user[1],
            email: user[2],
            phone: phone(user[3], user[4]),
          });
        });
      } else this.importData = [];

      this.importData = _.compact(this.importData);
      if (this.importData && this.importData[0].givenName === 'First Name') this.importData.splice(0, 1);
    },
    parseCsv: (file) => new Promise((resolve, reject) => Papa.parse(file, { complete: resolve, error: reject })),
    async doImport() {
      if (this.showProgress) {
        this.cancel();
        return;
      }

      const numUsers = this.importData.length;
      this.importing = true;
      this.showProgress = true;

      const queue = new PQueue({ concurrency: 5 });
      let count = 0;

      const progress = () => {
        this.importProgress = ++count / numUsers * 100;
        this.summary.imported = count - this.summary.existing - this.summary.failed.count;
      };
      const errorHandler = (error) => {
        if (error.code === 'EMPLOYEE-SERVICE-03') this.summary.existing++;
        else {
          this.trackingErrors.push(error);
          this.summary.failed.errors.push(error.displayMessage);
          this.summary.failed.count++;
        }
      };
      const mapper = (user) => {
        const data = { ...user, corporationIds: [this.corporation], errorHandler };
        const store = this.$store;
        return () => store.dispatch('employee/createEmployee', data);
      };

      queue.on('next', progress.bind(this));
      await queue.addAll(this.importData.map(mapper.bind(this)));

      this.importing = false;

      await BusinessBookerEvent.send({
        code: BusinessBookerEventCode.IMOPRT_EMPLOYEES_API_REQUEST_COMPLETED,
        pageUri: window.location.pathname,
        payload: {
          corporationId: this.corporation,
          summary: {
            imported: this.summary.imported,
            existing: this.summary.existing,
            failed: this.summary.failed.count,
          },
          errors: this.trackingErrors.length ? this.trackingErrors : undefined,
        },
      });
    },
    changeCorporation(corporation) {
      if (!corporation) return;

      this.corporation = corporation;
    },
    handleErrorDisplayMessage(error) {
      console.log(error);
      if (error === "Some business passengers couldn't be created.") return 'Business passenger already exists';
      if (error === "An unknown error occurred") return 'Some of required fields are missing';

      return error;
    },
  },
};
</script>

<style lang="sass" scoped>
.errors-list
  max-height: 300px
  overflow-y: auto
.label
  text-transform: uppercase
  font-size: 12px

.v-file-input::v-deep .v-input__prepend-outer
  margin-top: 12px !important

.v-btn
  text-transform: initial

.v-btn.cancel
  color: #888888

.v-btn.theme--dark.v-btn--disabled
  background-color: #272727 !important

.v-list.summary
  padding: 0

  .v-list-item
    min-height: initial

  .v-list-item__content
    padding: 6px 0
</style>
